// @ts-nocheck
import {useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import useGetProviderGames from "../../../hooks/games/useGetProviderGames.hook";
import useToggleProviderFollow from "../../../hooks/games/useToggleProviderFollow.hook";
import {useAuthStore} from "../../../store/useAuth.store";
import * as styles from "./_providerProfileCls";
import GamePostModal from "./components/GamePostModal";
import PostCard from "./components/PostCard";

const ProviderProfile = () => {
  const {t} = useTranslation();
  const {provider} = useParams();
  const navigate = useNavigate();
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);

  const authStatus = useAuthStore(state => state.authStatus);
  const {games, total, followerCount, isFollowed, isLoading} = useGetProviderGames(provider);
  const toggleFollow = useToggleProviderFollow(provider);
  const [selectedGameIndex, setSelectedGameIndex] = useState(null);
  const [viewMode, setViewMode] = useState("grid");

  const providerDisplayName = games?.[0]?.game_provider || provider || "";
  const providerInitial = providerDisplayName.charAt(0)?.toUpperCase() || "?";

  useEffect(() => {
    const footer = document.querySelector("footer");
    if (footer) footer.style.display = "none";
    return () => {
      if (footer) footer.style.display = "";
    };
  }, []);

  const handleRequestAuth = useCallback(() => {
    handleShowAuthModal();
  }, [handleShowAuthModal]);

  const handleFollowClick = useCallback(() => {
    if (authStatus !== "authenticated") {
      handleShowAuthModal();
      return;
    }
    toggleFollow.mutate();
  }, [authStatus, handleShowAuthModal, toggleFollow]);

  const handleGameClick = index => {
    setSelectedGameIndex(index);
  };

  const handleCloseModal = useCallback(() => {
    setSelectedGameIndex(null);
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className={styles.profilePageCls}>
        <div className={styles.profileContainerCls}>
          <div className={styles.topBarCls}>
            <button
              type="button"
              className={styles.backButtonCls}
              onClick={handleBack}
              aria-label={t("providerProfileBack")}
            >
              ←
            </button>
            <h1 className={styles.topBarTitleCls}>{provider}</h1>
          </div>
          <div className={styles.loadingContainerCls}>
            <div className={styles.loadingSpinnerCls} />
            <span className={styles.loadingTextCls}>{t("loading")}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.profilePageCls}>
      <div className={styles.profileContainerCls}>
        {/* Top bar */}
        <div className={styles.topBarCls}>
          <button
            type="button"
            className={styles.backButtonCls}
            onClick={handleBack}
            aria-label={t("providerProfileBack")}
          >
            ←
          </button>
          <h1 className={styles.topBarTitleCls}>{providerDisplayName}</h1>
        </div>

        {/* Profile header */}
        <div className={styles.profileHeaderCls}>
          <div className={styles.avatarRingCls}>
            <div className={styles.avatarInnerCls}>{providerInitial}</div>
          </div>
          <h2 className={styles.providerDisplayNameCls}>{providerDisplayName}</h2>
          <button
            type="button"
            className={`${styles.followButtonCls} ${isFollowed ? styles.followButtonActiveCls : ""}`}
            onClick={handleFollowClick}
            disabled={toggleFollow.isPending}
          >
            {isFollowed ? t("providerProfileFollowing") : t("providerProfileFollow")}
          </button>
        </div>

        {/* Stats row */}
        <div className={styles.statsRowCls}>
          <div className={styles.statItemCls}>
            <span className={styles.statValueCls}>{total}</span>
            <span className={styles.statLabelCls}>{t("providerProfileGames")}</span>
          </div>
          <div className={styles.statItemCls}>
            <span className={styles.statValueCls}>{followerCount}</span>
            <span className={styles.statLabelCls}>{t("providerProfileFollowers")}</span>
          </div>
        </div>

        {/* View toggle */}
        <div className={styles.viewToggleCls}>
          <button
            type="button"
            className={`${styles.viewToggleBtnCls} ${viewMode === "grid" ? styles.viewToggleBtnActiveCls : ""}`}
            onClick={() => setViewMode("grid")}
            aria-label={t("providerProfileGridView")}
          >
            ▦
          </button>
          <button
            type="button"
            className={`${styles.viewToggleBtnCls} ${viewMode === "list" ? styles.viewToggleBtnActiveCls : ""}`}
            onClick={() => setViewMode("list")}
            aria-label={t("providerProfileListView")}
          >
            ☰
          </button>
        </div>

        {/* Game content */}
        {games.length === 0 ? (
          <div className={styles.emptyStateCls}>
            <span className={styles.emptyIconCls}>🎮</span>
            <span className={styles.emptyTextCls}>{t("providerProfileNoGames")}</span>
          </div>
        ) : viewMode === "grid" ? (
          <div className={styles.gamesGridCls}>
            {games.map((game, index) => {
              const icon = game.custom_icon || game.icon;
              return (
                <div
                  key={game.game_id || index}
                  className={styles.gameGridItemCls}
                  onClick={() => handleGameClick(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === "Enter" && handleGameClick(index)}
                >
                  {icon ? (
                    <img
                      src={icon}
                      alt={game.game_name}
                      className={styles.gameGridImageCls}
                      loading="lazy"
                    />
                  ) : (
                    <div className={styles.gameGridPlaceholderCls}>{game.game_name}</div>
                  )}
                  <div className={styles.gameGridOverlayCls}>
                    {game.like_count > 0 && <span className={styles.gameGridStatCls}>❤️ {game.like_count}</span>}
                    {game.comment_count > 0 && <span className={styles.gameGridStatCls}>💬 {game.comment_count}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* List view — shows posts directly */
          <div>
            {games.map(game => (
              <div key={game.game_id}>
                <PostCard
                  game={game}
                  providerName={providerDisplayName}
                  providerInitial={providerInitial}
                  onRequestAuth={handleRequestAuth}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* IG-style post detail modal — reuses the full GameFeed experience */}
      {selectedGameIndex !== null && (
        <GamePostModal
          games={games}
          startIndex={selectedGameIndex}
          providerName={providerDisplayName}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default ProviderProfile;
